import type { Log } from './types'

import pipe from 'lodash/fp/pipe'

import debugLog from '@app/utils/debug-log'
import env from '@app/utils/environment'

const logToConsoleIfDev = (log: Log): Log => {
  if (!env.__DEV__ || env.__TEST__) {
    return log
  }

  if (log.level === 'error') {
    console.error(log.message, log.error)
  } else if (log.level === 'warning') {
    console.warn(log.message, log.extra)
  } else {
    console.log(log.message, log.extra)
  }

  return log
}

const logToDebug = (log: Log): Log => {
  debugLog(log.message, log.error, log.extra)
  return log
}

const logToSentry = (log: Log): Log => {
  if (!window.Sentry) {
    return log
  }

  const { level, message, error, extra } = log

  if (level === 'error') {
    window.Sentry.captureException(error, extra ? { extra } : undefined)
  } else {
    window.Sentry.captureMessage(message, {
      level,
      ...(extra && { extra }),
    })
  }

  return log
}

const SfLogInternal = (message: string, options: Omit<Log, 'message'>): Log =>
  pipe(
    logToDebug,
    logToConsoleIfDev,
    logToSentry
  )({
    ...options,
    message,
  })

/**
 * An adapter for logging client side. Logs to console, debugLog, and Sentry, depending on the environment.
 */
export const SfLog = {
  error: (message: string, options: Omit<Log, 'message' | 'level'> = {}): Log =>
    SfLogInternal(message, { ...options, level: 'error' }),
  info: (message: string, options: Omit<Log, 'message' | 'level'> = {}): Log =>
    SfLogInternal(message, { ...options, level: 'info' }),
  warn: (message: string, options: Omit<Log, 'message' | 'level'> = {}): Log =>
    SfLogInternal(message, { ...options, level: 'warning' }),
}
